/**
 * "Now on the tablet" strip at the top of Schedules (BA-85 S6): while anything is active it
 * says what, which is also what a new send would replace, and links to the live panel.
 */

import React from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowForward, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CurrentAction } from '../types/currentAction';
import { SentSchedule, isSentScheduleActive } from '../types/sentSchedule';
import { isChoiceBoard, stepPosition } from '../utils/sentSchedule';
import { RichText } from './RichText';

interface OnTabletStripProps {
  deviceName: string;
  sentSchedule: SentSchedule | null;
  currentAction: CurrentAction | null;
  actionName: string;
}

export const OnTabletStrip: React.FC<OnTabletStripProps> = ({
  deviceName,
  sentSchedule,
  currentAction,
  actionName,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  let line: React.ReactNode = null;
  if (sentSchedule && isSentScheduleActive(sentSchedule)) {
    const { n, total } = stepPosition(sentSchedule);
    line = (
      <RichText
        i18nKey={isChoiceBoard(sentSchedule) ? 'sentSchedule.strip.choice' : 'sentSchedule.strip.schedule'}
        values={{ device: deviceName, name: sentSchedule.schedule?.name ?? '', n, total }}
      />
    );
  } else if (currentAction) {
    line = <RichText i18nKey="sentSchedule.strip.action" values={{ device: deviceName, name: actionName }} />;
  }
  if (!line) {
    return null;
  }

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexDirection: isPhone ? 'column' : 'row',
        alignItems: isPhone ? 'stretch' : 'center',
        gap: isPhone ? 1.5 : 2,
        mb: 2,
        p: isPhone ? 1.5 : '8px 8px 8px 16px',
        bgcolor: '#e3f2fd',
        borderLeft: 4,
        borderColor: 'primary.main',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
        <PlayArrow color="primary" sx={{ mt: '1px' }} />
        <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{line}</Typography>
      </Box>
      <Button
        variant="outlined"
        endIcon={<ArrowForward />}
        onClick={() => navigate('/dashboard/controls')}
        sx={{ flexShrink: 0, bgcolor: 'background.paper', ...(isPhone ? { minHeight: 48 } : {}) }}
      >
        {t('sentSchedule.seeLive')}
      </Button>
    </Box>
  );
};
